import AllInviteFilters from 'Components/invites/AllInviteFilters';
import InvitesTable from 'Components/campaigns/invites/InvitesTable';
import FilterIcon from 'Components/icons/FilterIcon';
import { PRIVILEGE } from 'constants/privilege';
import withAccessControl from 'hoc/withAccessControl';
import useDocumentTitle from 'hooks/useDocumentTitle';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { defaultGlobalInvitesFilters, updateGlobalInvitesFilters } from 'slices/invites/reducer';
import { fetchInvitesThunk, INVITES_FILTER_KEY_EXCLUDES } from 'slices/invites/thunk';
import { AppDispatch, RootState } from 'store';
import { GlobalInvitesFilters } from 'types/invite';
import { decodeBase64, encodeBase64, generateUniqueKey } from 'utils/helpers';
import './style.scss';

const Invites = () => {
    useDocumentTitle('Invites');

    const dispatch: AppDispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    const [filtersModalVisibility, showFiltersModal] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    const queryFilters = searchParams.get('filters');
    useEffect(() => {
        const decodedFilters = queryFilters
            ? decodeBase64(queryFilters, defaultGlobalInvitesFilters)
            : defaultGlobalInvitesFilters;
        dispatch(updateGlobalInvitesFilters({ ...decodedFilters, page: 1 }));
    }, [queryFilters, dispatch]);

    const filters = useSelector((state: RootState) => state.invites.filters);
    const [filterId, setFilterId] = useState(generateUniqueKey(filters, INVITES_FILTER_KEY_EXCLUDES));

    const invitesData = useSelector((state: RootState) => state.invites.filtered_lists[filterId]);
    const invites = invitesData?.list ?? [];
    const page = invitesData?.page ?? 1;
    const hasMore = invitesData?.has_more ?? false;
    const count = invitesData?.count ?? 0;

    const loading = useSelector((state: RootState) => state.invites.loading);
    const error = useSelector((state: RootState) => state.invites.error);

    useEffect(() => {
        if (filters.page === 1) {
            setInitialLoading(true);
        }

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            dispatch(fetchInvitesThunk({ filters: { ...filters, page: filters.page ?? 1 } })).finally(() => {
                setFilterId(generateUniqueKey(filters, INVITES_FILTER_KEY_EXCLUDES));
                setInitialLoading(false);
            });
        }, 500);

        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [dispatch, filters]);

    const setFilters = (updatedFilters: GlobalInvitesFilters) => {
        const { page: _page, ...rest } = updatedFilters;
        const cleanedFilters = Object.fromEntries(
            Object.entries(rest).filter(([, value]) => value !== undefined && value !== null && value !== '')
        );

        if (Object.keys(cleanedFilters).length > 0) {
            setSearchParams({ filters: encodeBase64(cleanedFilters) });
        } else {
            setSearchParams({});
        }
    };

    const nextPage = () => {
        if (hasMore && !loading && invites.length > 0) {
            dispatch(
                fetchInvitesThunk({
                    filters: { ...filters, page: page + 1 },
                })
            );
        }
    };

    return (
        <div className="invites-page">
            <div className="invites-page-header">
                <h1 className="invites-page-title">Invites</h1>
                <button type="button" className="btn btn-white" onClick={() => showFiltersModal(true)}>
                    <FilterIcon className="me-1" /> Filter
                </button>
            </div>
            <div className="card campaign-insights-card invites-page-card">
                <div className="card-header d-flex justify-content-between align-items-center p-4 border-0">
                    <p className="invites-page-count">
                        {!initialLoading && invites.length > 0
                            ? `Showing ${invites.length} of ${count} Records`
                            : null}
                    </p>
                </div>
                <div className="card-body p-0 m-0 invites-table-wrap">
                    <InvitesTable
                        invites={invites}
                        hasMore={hasMore}
                        loading={loading}
                        initialLoading={initialLoading}
                        error={error}
                        onNextPage={nextPage}
                        showCampaignColumn
                        actionMode="dm"
                    />
                </div>
            </div>
            <AllInviteFilters
                isOpen={filtersModalVisibility}
                onClose={() => showFiltersModal(false)}
                defaultFilters={filters}
                onApply={(updatedFilters) => setFilters(updatedFilters)}
            />
        </div>
    );
};

export default withAccessControl(Invites, PRIVILEGE.CAMPAIGN.INVITE.INDEX);
